import Header from "../components/header";
import mainStore from "../../store/store";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/footer";
import Stepper from "../components/stepper";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { SkeletonTopCars } from "./skeleton";
import AvailabilityModal from "../../components/AvailabilityModal";
import VehicleDetailsDropdown from "../../components/VehicleDetailsDropdown";
import { getVehicleRateSummary } from "../../utils/vehicleRateLabels";
import {
  getAvailableFleetCategories,
  getCarsForFleetCategory,
} from "../../data/fleetCategories";
import "../../styles/carCards.css";

const TopCars = ({ categoryPageId = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedCarForAvailability, setSelectedCarForAvailability] = useState(null);
  
  const {
    getAllCars,
    userBooking,
    setUserBooking,
    visitedRoutes,
    setVisitedRoutes,
    setSelectedRoute,
    getCarImg,
    cachedCars,
    setCachedCars,
    getCarAvailabilityCalendar,
  } = mainStore();

  const routeTo = (routeTo) => {
    setSelectedRoute(routeTo);
    navigate(routeTo);
  };
  // Use cached data if available — no skeleton on revisit
  const [allCarsList, setAllCarsList] = useState(cachedCars.length > 0 ? cachedCars : []);
  const [loading, setLoading] = useState(cachedCars.length === 0);
  const availableCategories = useMemo(
    () => getAvailableFleetCategories(allCarsList),
    [allCarsList]
  );

  const selectedCategory = categoryPageId
    ? availableCategories.find((category) => category.id === categoryPageId) ||
      null
    : null;

  const selectedCategoryCars = useMemo(
    () =>
      selectedCategory
        ? getCarsForFleetCategory(allCarsList, selectedCategory.id)
        : [],
    [allCarsList, selectedCategory]
  );

  //   const carBookingCounts = futureBookings.reduce((acc, booking) => {
  //     acc[booking.car] = (acc[booking.car] || 0) + 1;
  //     return acc;
  //   }, {});
  //  const top3Cars = Object.entries(carBookingCounts)
  //   .sort((a, b) => b[1] - a[1]) // Sort by count descending
  //   .slice(0, 3) // Get top 3
  //   .map(([car, count]) => ({ car, count }));

  // console.log(top3Cars);

  const fromfind = searchParams.get("find");
  console.log("fromfind", fromfind);

  useEffect(() => {
    initializeCars();

    // --- Auto-refresh: poll every 60s + re-fetch on tab focus ---
    const pollInterval = setInterval(() => {
      refreshCars();
    }, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCars();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  /* Legacy admin availability request removed in favor of the public date-range calendar.
  const fetchAvailabilityData = async () => {
    try {
      const response = await axiosInstance.get("/cars/admin/availability", { timeout: 15000 });
      if (response.data && Array.isArray(response.data)) {
        setAvailabilityData(response.data);
        console.log('✅ Availability data fetched:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching availability data:', error);
    }
  };

  // Silent refresh — updates car data without showing skeleton
  */
  const refreshCars = async () => {
    try {
      const startDate = userBooking.booking.pickUpDate
        ? moment(userBooking.booking.pickUpDate).format("YYYY-MM-DD")
        : null;
      const endDate = userBooking.booking.returnDate
        ? moment(userBooking.booking.returnDate).format("YYYY-MM-DD")
        : null;
      const res = await getAllCars(startDate, endDate);
      if (res && Array.isArray(res) && res.length > 0) {
        const startDate = moment(userBooking.booking.pickUpDate);
        const endDate = moment(userBooking.booking.returnDate);
        const daysDiff = endDate.diff(startDate, "days");
        const inclusiveDays = daysDiff === 0 ? 1 : daysDiff;

        const updatedCars = res.map((car) => {
          const rates = car.rates;
          const unlimited_rates = car.unlimedRates;
          const rate = rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          const unlimited_rate = unlimited_rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          if (rate) {
            car.defaultRatePerDay = fromfind == 1 ? rate.rate * inclusiveDays : rate.rate;
            car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
            if (rate.originalRate) {
              car.originalRatePerDay = fromfind == 1 ? rate.originalRate * inclusiveDays : rate.originalRate;
            }
          }
          if (unlimited_rate) {
            car.defaultUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.rate * inclusiveDays : unlimited_rate.rate;
            car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
            if (unlimited_rate.originalRate) {
              car.originalUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.originalRate * inclusiveDays : unlimited_rate.originalRate;
            }
          }
          const isUnavailable = car.availabilityStatus === 'unavailable';
          const isSoldOut = car.isFullyBooked || car.availableNow === 0;
          let unavailableLabel = null;
          if (isUnavailable) {
            unavailableLabel = car.availabilityLabel === 'Sold Out'
              ? 'Unavailable'
              : (car.availabilityLabel || 'Coming Soon');
          } else if (isSoldOut) unavailableLabel = 'Unavailable';
          return { ...car, overlappingCount: car.activeBookings || 0, isSold: isSoldOut, isUnavailable, unavailableLabel };
        });
        setAllCarsList(updatedCars);
        setCachedCars(updatedCars);
        console.log('🔄 Cars silently refreshed');
      }
    } catch (error) {
      // Silent fail — don't disrupt the user
      console.warn('Silent refresh failed:', error.message);
    }
  };

  const handleViewAvailability = async (car) => {
    try {
      const selectedStartDate = userBooking.booking.pickUpDate;
      const selectedEndDate = userBooking.booking.returnDate;
      const calendarFrom = moment(selectedStartDate).startOf('day').format('YYYY-MM-DD');
      const calendarTo = moment(selectedEndDate).add(12, 'months').endOf('month').format('YYYY-MM-DD');
      const calendar = await getCarAvailabilityCalendar(car._id, calendarFrom, calendarTo);

      setSelectedCarForAvailability({
        ...car,
        bookedPeriods: calendar?.bookedPeriods || [],
        fullyBookedPeriods: calendar?.fullyBookedPeriods || [],
        nextAvailableDate: calendar?.nextAvailableDate || null,
        lastAvailableDate: calendar?.lastAvailableDate || null,
        totalQuantity: calendar?.totalQuantity,
      });
      setShowAvailabilityModal(true);
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  };

  const handleAvailabilityDatesSelected = ({ startDate, endDate }) => {
    const booking = {
      ...userBooking,
      booking: {
        ...userBooking.booking,
        pickUpDate: startDate,
        returnDate: endDate,
        days: Math.max(1, moment(endDate).diff(moment(startDate), 'days')),
      },
    };

    setUserBooking(booking);
    setCachedCars([]);
    setShowAvailabilityModal(false);
    window.location.assign('/cars?find=1');
  };

  const initializeCars = async () => {
    // Only show skeleton if no cached data
    if (cachedCars.length === 0) {
      setLoading(true);
    }
    const updatedRoutes = [...visitedRoutes];
    updatedRoutes.push("car");
    setVisitedRoutes(updatedRoutes);
    const startDate = moment(userBooking.booking.pickUpDate);
    const endDate = moment(userBooking.booking.returnDate);
    userBooking.booking.driverAge = 21;
    // Calculate days - minimum 1 day for same-day rentals
    const daysDiff = endDate.diff(startDate, "days");
    const inclusiveDays = daysDiff === 0 ? 1 : daysDiff;
    // Cars API uses date range to compute availability for user's dates
    const startDateStr = userBooking.booking.pickUpDate
      ? moment(userBooking.booking.pickUpDate).format("YYYY-MM-DD")
      : null;
    const endDateStr = userBooking.booking.returnDate
      ? moment(userBooking.booking.returnDate).format("YYYY-MM-DD")
      : null;
    const res = await getAllCars(startDateStr, endDateStr);
    // Safety check: ensure res is an array before mapping
    if (res && Array.isArray(res) && res.length > 0) {
      const bookedCars = res.map((car) => {
        const rates = car.rates;
        const unlimited_rates = car.unlimedRates;
        // car.type === "limited"
        //   ? car.rates
        //   : car.unlimedRates;

        const rate = rates.find(
          (rate) =>
            inclusiveDays >= rate.minDays &&
            (rate.maxDays === null || inclusiveDays <= rate.maxDays)
        );
        const unlimited_rate = unlimited_rates.find(
          (rate) =>
            inclusiveDays >= rate.minDays &&
            (rate.maxDays === null || inclusiveDays <= rate.maxDays)
        );
        
        // Always calculate prices based on rate tiers
        if (rate) {
          car.defaultRatePerDay = fromfind == 1 ? rate.rate * inclusiveDays : rate.rate;
          car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
          // Store original price if discount was applied
          if (rate.originalRate) {
            car.originalRatePerDay = fromfind == 1 ? rate.originalRate * inclusiveDays : rate.originalRate;
          }
        } else {
          // Fallback to default if no matching tier
          car.displayRateLabel = "Daily Rate";
        }
        
        if (unlimited_rate) {
          car.defaultUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.rate * inclusiveDays : unlimited_rate.rate;
          car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
          // Store original price if discount was applied
          if (unlimited_rate.originalRate) {
            car.originalUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.originalRate * inclusiveDays : unlimited_rate.originalRate;
          }
        } else {
          // Fallback to default if no matching tier
          car.displayUnlimitedRateLabel = "Daily Rate";
        }

        console.log("userBooking", userBooking.booking);
        // Use server-provided availability instead of client-side overlap calculation
        const isUnavailable = car.availabilityStatus === 'unavailable';
        const isSoldOut = car.isFullyBooked || car.availableNow === 0;
        
        // Get the display label for unavailable cars
        let unavailableLabel = null;
        if (isUnavailable) {
          unavailableLabel = car.availabilityLabel === 'Sold Out'
            ? 'Unavailable'
            : (car.availabilityLabel || 'Coming Soon');
        } else if (isSoldOut) {
          unavailableLabel = 'Unavailable';
        }
        
        return {
          ...car,
          overlappingCount: car.activeBookings || 0,
          isSold: isSoldOut,
          isUnavailable,
          unavailableLabel,
        };
      });

      setAllCarsList(bookedCars);
      setCachedCars(bookedCars);
    }
    console.log("initializeCars ~ res:", res);
    setLoading(false);
  };

  const onSelectingCar = (car, type) => {
    let booking = { ...userBooking };
    booking.car = { ...car, type: type };
    
    // Store the EXACT price shown to user (this preserves discounts)
    const selectedPrice = type === "limited" 
      ? car.defaultRatePerDay 
      : car.defaultUnlimitedRatePerDay;
    
    // Store the rate tiers with discounts already applied
    booking.selectedRates = type === "limited" ? car.rates : car.unlimedRates;
    booking.selectedType = type;
    booking.hasDiscount = !!car.activeDiscount;
    
    console.log('🚗 Car Selected:', {
      car: car.name,
      type: type,
      displayPrice: selectedPrice,
      hasDiscount: booking.hasDiscount,
      ratesStored: booking.selectedRates?.length || 0
    });
    
    setUserBooking(booking);
    navigate(`/booking?id=${car._id}`);
  };

  // const getCarImg = (car) => {
  //   if (car.name === "Suzuki Swift") {
  //     return "img/cars/suzuki_swift.png";
  //   } else if (car.name === "Mazda CX3") {
  //     return "img/cars/mazda_cx3.png";
  //   }
  //   else if (car.name === "Mazda CX8") {
  //     return "img/cars/mazda_cx8.png";
  //   } else if (car.name === "Toyota Camry") {
  //     return "img/cars/toyota_camry.png";
  //   } else if (car.name === "Toyota Hilux") {
  //     return "img/cars/hilux.png";
  //   } else if (car.name === "Toyota Corolla") {
  //     return "img/cars/toyota_corrolla.png";
  //   } else if (car.name === "Outlander") {
  //     return "img/cars/mitsubishi_out.png";
  //   } else if (car.name === "Tesla Model 3") {
  //     return "img/cars/car_6.png";
  //   }
  // };

  // Show skeleton while loading
  if (loading) {
    return <SkeletonTopCars />;
  }

  return (
    <>
      <section
        className="FleetCategorySection"
        aria-labelledby="home-fleet-categories-heading"
      >
        <div className="container">
          <div className="FleetCategoryHeading">
            <span className="FleetCategoryEyebrow">TAK8 CAR RENTAL FLEET</span>
            <h2 id="home-fleet-categories-heading">
              Explore Car Rental Categories in Perth
            </h2>
            <p>
              Compare small cars, sedans, SUVs, people movers and utes
              available from TAK8 Car Rental in East Perth.
            </p>
          </div>

          <nav
            className="FleetCategoryGrid"
            aria-label="Rental vehicle categories"
          >
            {availableCategories.map((category) => {
              const isActive = categoryPageId === category.id;
              const categoryImage = category.representativeCar
                ? getCarImg(category.representativeCar)
                : null;

              return (
                <a
                  href={category.path}
                  className={`FleetCategoryCard ${isActive ? "is-active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                  key={category.id}
                >
                  <div className="FleetCategoryCardTop">
                    <span>{category.label}</span>
                    <small>
                      {category.vehicleCount}{" "}
                      {category.vehicleCount === 1 ? "vehicle" : "vehicles"}
                    </small>
                  </div>
                  <div className="FleetCategoryImage">
                    {categoryImage && (
                      <img
                        src={categoryImage}
                        alt={`${category.label} available from TAK8 Car Rental`}
                      />
                    )}
                  </div>
                  <div className="FleetCategoryCardBottom">
                    <span>{category.linkLabel}</span>
                    {category.fromPrice && (
                      <strong>From ${category.fromPrice}/day</strong>
                    )}
                  </div>
                </a>
              );
            })}
          </nav>

          {!categoryPageId && (
            <div className="FleetCategoryHomeCta">
              <p>
                Select a category to view its vehicles, current rates, rental
                information and frequently asked questions.
              </p>
              <a href="/carslist" className="BtnFill">
                View All Vehicles
              </a>
            </div>
          )}

          {categoryPageId && selectedCategory && (
            <div
              className="FleetCategoryResults"
              id={`fleet-${selectedCategory.id}`}
              aria-labelledby="home-fleet-categories-heading"
            >
              <div className="FleetCategoryResultsHeading">
                <div>
                  <span>{selectedCategory.eyebrow}</span>
                  <h3>{selectedCategory.resultsTitle}</h3>
                </div>
                <p>{selectedCategory.summary}</p>
              </div>

          {/* <Stepper /> */}
              <div className="row fade-in justify-content-center FleetVehicleGrid">
            {selectedCategoryCars.map((car, index) => (
              <div className="col-lg-4 col-md-6" key={car._id || index}>
                <div class={`CarBox ${car.isUnavailable ? 'car-unavailable' : ''}`}>
                  {/* Discount Banner - Keep this */}
                  {!car.isUnavailable && car.activeDiscount && (
                    <div className="discount-banner-header">
                      <span className="discount-name">{car.activeDiscount.name}</span>
                      <span className="discount-percentage">{car.activeDiscount.badge}</span>
                    </div>
                  )}
                  <div class="Image">
                    <img
                      src={getCarImg(car)}
                      alt={`${car.name} rental vehicle from TAK8 Car Rental`}
                    />
                    {car.isAutomatic ? (
                      <span class="Tag Red">Automatic</span>
                    ) : (
                      <span class="Tag Yellow">Manual</span>
                    )}
                    {/* <span class="Tag Red" onClick={() => setPopupOpen(true)}>Details</span> */}
                    <span class="Feature">
                      <img src="/img/icon/carseat.svg" alt="" aria-hidden="true" /> {car.seats}{" "}
                      Seats |
                      <img src="/img/icon/bags.svg" alt="" aria-hidden="true" /> {car.bags} Bags
                    </span>
                  </div>
                  <div class="Content">
                    <VehicleDetailsDropdown car={car} />
                    <div className="PriceMain">
                      <div class="Price">
                        <div>
                          <span className="rate-type-label">Limited kilometres</span>
                          {car.originalRatePerDay && !car.isUnavailable && (
                            <span className="savings">Save ${(car.originalRatePerDay - car.defaultRatePerDay).toFixed(2)}</span>
                          )}
                          {car.originalRatePerDay && !car.isUnavailable ? (
                            <>
                              <span className="original-price rate-original-daily">
                                ${car.originalRatePerDay}/day
                              </span>
                              <div className="rate-price-line">
                                <span className="rate-from">From</span>
                                <strong className="discounted-price">${car.defaultRatePerDay}</strong>
                                <span className="rate-per-day">/day</span>
                              </div>
                            </>
                          ) : (
                            <div className="rate-price-line">
                              <span className="rate-from">From</span>
                              <strong>${car.defaultRatePerDay || "0.00"}</strong>
                              <span className="rate-per-day">/day</span>
                            </div>
                          )}
                          <span className="rate-label rate-summary">
                            {getVehicleRateSummary(
                              car.displayRateLabel,
                              "150 km/day"
                            )}
                          </span>
                        </div>
                        <a
                          href={`/booking?id=${car._id}`}
                          onClick={(event) => {
                            event.preventDefault();
                            if (!car.isUnavailable && !car.isSold) {
                              onSelectingCar(car, "limited");
                            }
                          }}
                          class={`BtnBorderY cursor-pointer ${
                            (car.isSold || car.isUnavailable) ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Unavailable" : "Book This Car")}
                        </a>
                        {car.isSold && (
                          <span 
                            className="view-availability-link"
                            onClick={() => handleViewAvailability(car)}
                          >
                            Check Availability
                          </span>
                        )}
                      </div>
                      <div class="Price">
                        <div>
                          <span className="rate-type-label">Unlimited kilometres</span>
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable && (
                            <span className="savings">Save ${(car.originalUnlimitedRatePerDay - car.defaultUnlimitedRatePerDay).toFixed(2)}</span>
                          )}
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable ? (
                            <>
                              <span className="original-price rate-original-daily">
                                ${car.originalUnlimitedRatePerDay}/day
                              </span>
                              <div className="rate-price-line">
                                <span className="rate-from">From</span>
                                <strong className="discounted-price">${car.defaultUnlimitedRatePerDay}</strong>
                                <span className="rate-per-day">/day</span>
                              </div>
                            </>
                          ) : (
                            <div className="rate-price-line">
                              <span className="rate-from">From</span>
                              <strong>${car.defaultUnlimitedRatePerDay || "0.00"}</strong>
                              <span className="rate-per-day">/day</span>
                            </div>
                          )}
                          <span className="rate-label rate-summary">
                            {getVehicleRateSummary(
                              car.displayUnlimitedRateLabel,
                              "Unlimited kilometres"
                            )}
                          </span>
                        </div>
                        <a
                          href={`/booking?id=${car._id}`}
                          onClick={(event) => {
                            event.preventDefault();
                            if (!car.isUnavailable && !car.isSold) {
                              onSelectingCar(car, "unlimited");
                            }
                          }}
                          class={`BtnFill cursor-pointer ${
                            (car.isSold || car.isUnavailable) ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Unavailable" : "Book This Car")}
                        </a>
                        {car.isSold && (
                          <span 
                            className="view-availability-link"
                            onClick={() => handleViewAvailability(car)}
                          >
                            Check Availability
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
              <div className="row pt-4">
            <div className="Button text-center">
              <a
                href="/carslist"
                onClick={(event) => {
                  event.preventDefault();
                  routeTo("/carslist");
                }}
                class="BtnFill"
              >
                View All Vehicles
              </a>
            </div>
          </div>

              <div className="FleetCategoryContent">
                <div className="FleetCategoryCopy">
                  <span>{selectedCategory.eyebrow}</span>
                  <h3>About {selectedCategory.title}</h3>
                  <p>{selectedCategory.description}</p>
                  <p>{selectedCategory.secondaryDescription}</p>
                  <ul>
                    {selectedCategory.benefits.map((benefit) => (
                      <li key={benefit}>{benefit}</li>
                    ))}
                  </ul>
                  <a href="/carslist" className="FleetCategoryTextLink">
                    Browse all rental vehicles <span aria-hidden="true">→</span>
                  </a>
                </div>

                <div
                  className="FleetCategoryFaqs"
                  aria-labelledby={`${selectedCategory.id}-faq-heading`}
                >
                  <span>HELP &amp; INFORMATION</span>
                  <h3 id={`${selectedCategory.id}-faq-heading`}>
                    {selectedCategory.faqTitle}
                  </h3>
                  {selectedCategory.faqs.map((faq) => (
                    <details key={faq.question}>
                      <summary>{faq.question}</summary>
                      <p>{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      
      <AvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        car={selectedCarForAvailability}
        selectedStartDate={userBooking.booking.pickUpDate}
        selectedEndDate={userBooking.booking.returnDate}
        onDatesSelected={handleAvailabilityDatesSelected}
      />
    </>
  );
};

export default TopCars;
